import { NgClass, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, NgZone, Output, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent, MatCardActions } from '@angular/material/card';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatList, MatListItem, MatListItemTitle, MatListItemMeta } from '@angular/material/list';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { CommonIPLite, DisplayableFieldResponse, IP, ReferenceDetails, WF, WorkflowDO, WorkflowTaskDO, WsFile } from '@model/ftf-api.model';
import { FirstToFileFeatureFlags } from '@model/widget.model';
import { AccountService } from '@service/account.service';
import { ApiService } from '@service/api.service';
import { DataTransformService } from '@service/data-transform.service';
import { getTrifoldDocIcon } from '@utils/file-utils';
import { from, map, mergeMap, toArray } from 'rxjs';

import { IpDetailComponent } from '../ip-detail/ip-detail.component';

export type WorkflowDetailType = 'lite' | 'full';

/**
 * Details of a file associated with a workflow.
 */
export interface WorkflowFileDetail {
  id: number;
  primaryDisplay: string;
  secondaryDisplay: string;
  type: IP.TypeEnum;
  raw: WsFile | ReferenceDetails;
}

@Component({
  selector: 'workflow-detail',
  templateUrl: './workflow-detail.component.html',
  styleUrl: './workflow-detail.component.scss',
  imports: [
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardSubtitle,
    MatCardContent,
    NgClass,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    IpDetailComponent,
    MatList,
    MatListItem,
    MatListItemTitle,
    MatTooltip,
    MatListItemMeta,
    MatMenuTrigger,
    MatIcon,
    MatCardActions,
    MatButton,
    DatePipe,
  ],
})
export class WorkflowDetailComponent {
  private ngZone = inject(NgZone);
  private apiService = inject(ApiService);
  private accountService = inject(AccountService);
  private dataTransformService = inject(DataTransformService);

  @Input({ required: true }) item!: CommonIPLite;
  @Input({ required: true }) workflow!: WorkflowDO;
  @Output() workflowChange = new EventEmitter<WorkflowDO>();
  @Output() docEvent = new EventEmitter<{ actionType: 'icon' | 'text'; record: WorkflowFileDetail }>();
  @Input() type: WorkflowDetailType = 'lite';
  @Input({ required: true }) menu!: MatMenu;

  detail: DisplayableFieldResponse | null = null;
  files: WorkflowFileDetail[] = [];

  /**
   * Handles workflow action click events.
   * @param event The mouse event.
   * @param workflow The workflow object.
   * @param action The action to perform.
   */
  onWorkflowActionClickEvent(event: MouseEvent, workflow: WorkflowDO, action: WF.ActionEnum) {
    event.stopPropagation();
    this.apiService.updateWorkflow(workflow.id!, action).subscribe((workflow) => {
      // update workflow in the item
      this.workflowChange.emit(workflow);
    });
  }

  /**
   * Toggles the workflow detail view between lite and full.
   * @param event The mouse event.
   */
  onWorkflowDetailToggle(event: MouseEvent) {
    event.stopPropagation();
    if (this.type === 'lite') {
      this.type = 'full';
      this.initWorkflowDetail();
    }
  }

  /**
   * Initializes the workflow detail by converting the workflow to a displayable format
   * and fetching all associated files.
   */
  initWorkflowDetail() {
    // convert the workflow to a displayable format
    this.ngZone.run(() => {
      this.detail = this.dataTransformService.displayableWorkflow(this.workflow);
    });
    // find all files associated with this workflow
    this.apiService
      .findWorkflowFiles(this.workflow.id!)
      .pipe(
        mergeMap((files) => {
          return from(files.filter((f) => f.fileId !== 0)).pipe(
            mergeMap((file) => {
              // get the file detail (i.e. titles, filename, etc..)
              return file.type === IP.TypeEnum.Reference
                ? this.apiService.findReferenceDetails(file.fileId).pipe(
                  map(
                    (ref) =>
                        ({
                          raw: ref,
                          id: file.fileId,
                          primaryDisplay: ref.titleDisplay,
                          secondaryDisplay: ref.referenceTypeDisplay,
                          type: file.type,
                        }) as WorkflowFileDetail
                  )
                )
                : this.apiService.findFileDetails(file.fileId, file.type).pipe(
                  map((doc) => {
                    const detail = [];
                    if (doc.documentDate) detail.push(doc.documentDate);
                    if (doc.docSubtype) detail.push(doc.docSubtype);
                    return {
                      raw: doc,
                      id: file.fileId,
                      primaryDisplay: doc.title || doc.fileName,
                      secondaryDisplay: detail.join(' | '),
                      type: file.type,
                    } as WorkflowFileDetail;
                  })
                );
            }, 2),
            toArray()
          );
        })
      )
      .subscribe((files) =>
        this.ngZone.run(() => {
          this.files = files;
        })
      );
  }

  /**
   * Handles file events within the workflow.
   * @param event The mouse event.
   * @param actionType The type of action (icon or text).
   * @param record The workflow file detail record.
   */
  onWorkflowFileEvent(event: MouseEvent, actionType: 'icon' | 'text', record: WorkflowFileDetail) {
    event.stopPropagation();
    this.docEvent.emit({ actionType, record: record });
  }

  /**
   * Get the icon name for a document based on its file name.
   * @param doc
   * @returns
   */
  getDocIcon(doc: WorkflowFileDetail) {
    return getTrifoldDocIcon(doc.raw.fileName!);
  }

  /**
   * Checks if the current user has admin role.
   * @returns True if the user has admin role, false otherwise.
   */
  isAdmin() {
    return this.accountService.hasRole('ROLE_ADMINISTRATION_ACCESS');
  }

  /**
   * Checks if the given workflow task is assigned to the current user.
   * @param task The workflow task to check.
   * @returns True if the task is assigned to the current user, false otherwise.
   */
  isWFAssignedToMe(task: WorkflowTaskDO) {
    return task.assignedTo
      ? task.assignedTo.firstName === this.accountService.firstName && task.assignedTo.lastName === this.accountService.lastName
      : false;
  }

  /**
   * Checks if a feature is enabled based on the provided key.
   * @param key The key of the FirstToFile feature flag.
   * @returns True if the feature is enabled, false otherwise.
   */
  isFeatureEnabled(key: keyof FirstToFileFeatureFlags): boolean {
    return this.accountService.featureFlags[key];
  }
}
